import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { User } from 'app/core/user/user.types';
import {
    careUserRole,
    fitnessInstructorRole,
    nullRole,
    salesUserRole,
    userToUserRoleMapping
} from "../models/users.constants";
import { NavBarRoute, UserPermissionModules, UserRole } from "../models/users.models";

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private _user: BehaviorSubject<User> = new BehaviorSubject<User>(undefined);
    private _appNeedsUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _wsConnectionID: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
    private _queryCount: BehaviorSubject<number> = new BehaviorSubject<number>(undefined);
    BASE_URL = 'https://curelinktech.in';

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User) {
        // Store the value
        this._user.next(value);
    }

    get user(): User {
        return this._user.value;
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    set appNeedsUpdate(value: boolean) {
        this._appNeedsUpdate.next(value);
    }

    get appNeedsUpdate$(): BehaviorSubject<boolean> {
        return this._appNeedsUpdate;
    }

    set queryCount(value: number) {
        this._queryCount.next(value);
    }

    get queryCount$(): BehaviorSubject<number> {
        return this._queryCount;
    }


    /**
     * Setter & getter for WS connection ID
     */
    set wsConnectionID(connectionID: string) {
        this._wsConnectionID.next(connectionID);
    }

    get wsConnectionID(): string {
        return this._wsConnectionID.value;
    }

    get wsConnectionID$(): BehaviorSubject<string> {
        return this._wsConnectionID;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current logged in user data
     */
    get(): Observable<User> {
        return this._httpClient.get<User>('api/common/user').pipe(
            tap((user) => {
                this._user.next(user);
            })
        );
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: User): Observable<any> {
        return this._httpClient.patch<User>('api/common/user', { user }).pipe(
            map((response) => {
                this._user.next(response);
            })
        );
    }

    private _fetchUserRole(): UserRole {
        if (this.user === undefined) {
            throw Error('User is not available');
        }
        if (userToUserRoleMapping[this.user.id] !== undefined) {
            return userToUserRoleMapping[this.user.id];
        } else if (this.user.speciality_id === 'd828790d-9770-49cf-aba9-4f054bc03d1c') {
            return careUserRole;
        } else if (this.user.speciality_id === '48bc4e0b-a70d-491b-bb56-3e8d68d6e2ee') {
            return salesUserRole;
        } else if (this.user.speciality_id === '94f41442-a112-47d4-ab1e-6d5b2f78bc2c'){
            return fitnessInstructorRole;
        }
        return nullRole;
    }

    private _isFeatureAvailable(role: UserRole, module: UserPermissionModules, feature: string): boolean {
        if (role.module_permissions[module]?.features?.has(feature)) {
            return true;
        }
        if (role.inherits_from) {
            for (let inheritedRole of role.inherits_from) {
                if (this._isFeatureAvailable(inheritedRole, module, feature)) {
                    return true;
                }
            }
        }
        return false;
    }

    isFeatureAvailable(module: UserPermissionModules, feature: string): boolean {
        let userRole = this._fetchUserRole();
        return this._isFeatureAvailable(userRole, module, feature)
    }

    private _fetchAvailableRoutes(role: UserRole): NavBarRoute[] {
        let availableRoutes: Set<NavBarRoute> = new Set<NavBarRoute>(Array.from(role.routes));
        if (role.inherits_from) {
            for (let inheritedRole of role.inherits_from) {
                for (let inheritedRoute of this._fetchAvailableRoutes(inheritedRole)) {
                    availableRoutes.add(inheritedRoute);
                }
            }
        }
        return Array.from(availableRoutes);
    }

    fetchAvailableRoutes(): NavBarRoute[] {
        let userRole = this._fetchUserRole();
        return this._fetchAvailableRoutes(userRole);
    }

    fetchDefaultRoute(): string {
        let userRole = this._fetchUserRole();
        return userRole.default_route;
    }
}
